import { useState } from "react";
import { useRouteError, useSearchParams } from "react-router-dom";

type ErrorDetails = {
  title: string;
  message: string;
  stack?: string;
};

// react-router hands route errors to useRouteError(). Our auth flow instead
// redirects to /auth-error?... with the details in the query string (because
// the failure happens in an un-awaited async callback that errorElement can't
// catch). Normalize both sources into one shape.
const useErrorDetails = (): ErrorDetails => {
  const routeError = useRouteError() as
    | { statusText?: string; message?: string; stack?: string; data?: unknown }
    | undefined;
  const [params] = useSearchParams();

  if (params.has("auth_error")) {
    return {
      title: "Sign-in failed",
      message: params.get("auth_error") || "Unknown authentication error.",
      stack: params.get("auth_detail") || undefined,
    };
  }

  if (routeError) {
    return {
      title: "Oops!",
      message:
        routeError.statusText ||
        routeError.message ||
        "An unexpected error has occurred.",
      stack: routeError.stack,
    };
  }

  return { title: "Oops!", message: "An unexpected error has occurred." };
};

function ErrorPage() {
  const { title, message, stack } = useErrorDetails();
  const [expanded, setExpanded] = useState(false);

  // Keep the console.error for devtools; the page shows it too.
  console.error(title, message, stack);

  return (
    <div
      id="error-page"
      className="d-flex flex-column align-items-center justify-content-center p-4 w-100"
      style={{ minHeight: "60vh" }}
    >
      <h1>{title}</h1>
      <p>Sorry, something went wrong.</p>
      <p className="text-danger fw-semibold" style={{ maxWidth: "48rem" }}>
        {message}
      </p>

      <div className="d-flex gap-2 mb-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.location.assign("/")}
        >
          Return home
        </button>
        {stack ? (
          <button
            type="button"
            className="btn btn-outline-secondary"
            aria-expanded={expanded}
            aria-controls="error-details"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        ) : null}
      </div>

      {expanded && stack ? (
        <pre
          id="error-details"
          className="bg-dark text-light p-3 rounded w-100"
          style={{
            maxWidth: "48rem",
            maxHeight: "40vh",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            textAlign: "left",
          }}
        >
          {stack}
        </pre>
      ) : null}
    </div>
  );
}

export default ErrorPage;
